import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Check, Plus, X } from 'lucide-react';

const STEPS = ['sell.step1', 'sell.step2', 'sell.step3'];

export default function Sell() {
  const { t, api } = useApp();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', designer: '', category: 'Handbags', description: '', estimated_value: '', photos: [] });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      update('photos', [...(form.photos || []), `${process.env.REACT_APP_BACKEND_URL}${data.url}`]);
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); }
  };
  const canNext = () => {
    if (step === 0) return form.designer && form.category && form.description;
    if (step === 1) return true;
    return form.name && form.email;
  };

  const submit = async () => {
    try {
      await api.post('/consignments', { ...form, estimated_value: form.estimated_value ? Number(form.estimated_value) : null, photos: form.photos || [] });
      setSubmitted(true);
      toast.success(t('sell.received'));
    } catch (e) {
      toast.error('Submission failed');
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="sell-success">
        <Check size={48} strokeWidth={1} className="mx-auto text-accent" />
        <h1 className="font-display text-4xl mt-8">Submission Received</h1>
        <p className="mt-4 font-light text-muted-foreground">{t('sell.received')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="sell-page">
      <span className="overline text-accent">For Sellers</span>
      <h1 className="font-display text-4xl sm:text-5xl mt-3">{t('sell.title')}</h1>
      <p className="mt-4 font-light text-muted-foreground max-w-xl">Earn up to 85% commission. Insured shipping, expert authentication, white-glove service.</p>

      {/* Stepper */}
      <div className="mt-14 flex items-center gap-6" data-testid="stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-3 ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>
              <span className={`w-8 h-8 inline-flex items-center justify-center border ${i <= step ? 'border-foreground bg-foreground text-background' : 'border-border'} overline`}>{`0${i+1}`}</span>
              <span className="overline hidden sm:inline">{t(s)}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-foreground' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-12 border-t border-border pt-10">
        {step === 0 && (
          <div className="space-y-6">
            <h2 className="font-display text-2xl">{t('sell.step1')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className="overline">Designer / Brand</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" value={form.designer} onChange={(e) => update('designer', e.target.value)} placeholder="e.g. Chanel" data-testid="sell-designer" />
              </div>
              <div>
                <Label className="overline">Category</Label>
                <select className="mt-2 w-full bg-transparent border-0 border-b border-foreground py-2 font-light" value={form.category} onChange={(e) => update('category', e.target.value)} data-testid="sell-category">
                  {['Handbags', 'Watches', 'Jewelry', 'Shoes', 'Women', 'Men', 'Accessories'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="overline">Description & Condition</Label>
                <Textarea className="mt-2 rounded-none border border-foreground focus-visible:ring-0" rows={5} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Tell us about your item — model, year acquired, condition…" data-testid="sell-description" />
              </div>
              <div>
                <Label className="overline">Estimated Value (optional)</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" type="number" value={form.estimated_value} onChange={(e) => update('estimated_value', e.target.value)} placeholder="USD" data-testid="sell-value" />
              </div>
              <div className="sm:col-span-2">
                <Label className="overline">Photos (optional)</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {(form.photos || []).map((src, i) => (
                    <div key={i} className="relative w-24 h-28 bg-muted">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => update('photos', form.photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-foreground text-background rounded-full p-1"><X size={10} /></button>
                    </div>
                  ))}
                  <label className="w-24 h-28 border border-dashed border-foreground flex items-center justify-center cursor-pointer hover:bg-secondary transition" data-testid="sell-photo-upload">
                    <Plus size={16} strokeWidth={1.4} />
                    <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <h2 className="font-display text-2xl">{t('sell.step2')}</h2>
            <p className="font-light text-muted-foreground">Our specialists will review your submission and respond within 48 hours with next steps for authentication.</p>
            <ul className="space-y-3 text-sm font-light">
              <li className="flex gap-3"><Check size={16} className="text-accent mt-0.5" /> Free shipping label with insurance</li>
              <li className="flex gap-3"><Check size={16} className="text-accent mt-0.5" /> Multi-point physical inspection</li>
              <li className="flex gap-3"><Check size={16} className="text-accent mt-0.5" /> Brand & serial verification</li>
              <li className="flex gap-3"><Check size={16} className="text-accent mt-0.5" /> Professional photography</li>
            </ul>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h2 className="font-display text-2xl">{t('sell.step3')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className="overline">{t('auth.name')}</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" value={form.name} onChange={(e) => update('name', e.target.value)} data-testid="sell-name" />
              </div>
              <div>
                <Label className="overline">{t('auth.email')}</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="sell-email" />
              </div>
              <div className="sm:col-span-2">
                <Label className="overline">Phone (optional)</Label>
                <Input className="mt-2 rounded-none border-0 border-b border-foreground focus-visible:ring-0" value={form.phone} onChange={(e) => update('phone', e.target.value)} data-testid="sell-phone" />
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 flex justify-between">
          <button disabled={step === 0} onClick={() => setStep((s) => s - 1)} className="overline disabled:opacity-30 link-underline" data-testid="sell-back">← {t('cta.back')}</button>
          {step < 2 ? (
            <button disabled={!canNext()} onClick={() => setStep((s) => s + 1)} className="bg-foreground text-background px-8 py-4 overline disabled:opacity-40" data-testid="sell-next">{t('cta.continue')} →</button>
          ) : (
            <button disabled={!canNext()} onClick={submit} className="bg-foreground text-background px-8 py-4 overline disabled:opacity-40" data-testid="sell-submit">{t('sell.submit')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
